/* ========================================
   AJAX FILTER FUNCTIONALITY
   Extracted from collection-filter.js
   ======================================== */

(function() {
  'use strict';

  // Access dependencies from global scope (exposed by collection-filter.js)
  const CONFIG = window.FilterUtils?.CONFIG;
  const Utils = window.FilterUtils?.Utils;
  const AjaxHandler = window.AjaxHandler;

  if (!CONFIG || !Utils || !AjaxHandler) {
    console.error('[filter-ajax.js] Dependencies not found. Ensure collection-filter.js is loaded first.');
    return;
  }

  // ========================================
  // AJAX FILTER FUNCTIONALITY
  // ========================================
  
  const AjaxFilter = {
    init() {
      // Initialize debounced handler now that Utils is available
      if (!this.debouncedFetchHandler && Utils) {
        this.debouncedFetchHandler = Utils.debounce((href) => {
          AjaxFilter.handleFilterWithFetch(href);
        }, 200);
      }
      
      // Don't bind events here - FilterSelectionManager handles all filter clicks
      // This prevents conflicts with the multi-select system
    },

    bindEvents() {
      // Events are now handled by FilterSelectionManager
      // This method is kept for backward compatibility but does nothing
    },

    handleFilterTagClick(link) {
      const href = link.getAttribute('href');
      
      // Use debounced wrapper that preserves context
      if (this.debouncedFetchHandler) {
        this.debouncedFetchHandler(href);
      } else {
        // Fallback if not initialized yet
        this.handleFilterWithFetch(href);
      }
    },

    debouncedFetchHandler: null, // Will be initialized after Utils is available

    async handleFilterWithFetch(href) {
      try {
        // Use optimized AJAX handler with caching and retry logic
        const result = await AjaxHandler.getFilteredContent(href);

        if (result) {
          // Update URL
          window.history.replaceState(null, null, href);
          
          // Efficient content update
          await this.updateContentOptimized(result);
          
          // Fast component reinitialization
          requestAnimationFrame(() => {
            this.reinitializeComponents();
            if (typeof window.LoadingManager !== 'undefined') {
              window.LoadingManager.hideCollectionLoading();
            }
            
            // Dispatch custom event for other components
            document.dispatchEvent(new CustomEvent('filterContentLoaded', {
              detail: { url: href, result }
            }));
          });
        } else {
          throw new Error('No content received from server');
        }
      } catch (error) {
        Utils.error('Failed to load filter results:', error);
        if (typeof window.LoadingManager !== 'undefined') {
          window.LoadingManager.hideCollectionLoading();
        }
        
        // Fallback to full page reload only for critical errors
        if (!navigator.onLine || error.message.includes('HTTP 5')) {
          window.location.href = href;
        }
      }
    },

    async updateContentOptimized(result) {
      try {
        // Update main collection content efficiently
        if (result.collection) {
          const existingCollection = document.querySelector('.collection');
          if (existingCollection) {
            Utils.updateDOMEfficiently(existingCollection, result.collection.innerHTML);
          }
        }
        // Update products section if available. Only target main grid inside #ProductGridContainer (never header/sidebar carousel with .product-list).
        if (result.products) {
          const grid = document.getElementById('ProductGridContainer');
          const existingProducts = (grid && (grid.querySelector('.collection__main') || grid.querySelector('.collection .product-list') || grid.querySelector('.collection__products'))) || document.querySelector('#ProductGridContainer .collection__main') || document.querySelector('.collection__main') || document.querySelector('.collection .product-list:not(.product-list--carousel)') || document.querySelector('.collection__products');
          if (existingProducts) {
            Utils.updateDOMEfficiently(existingProducts, result.products.innerHTML);
          } else {
            // Direct load with multi-tag URL: no .collection__main (server rendered "no results"). Replace entire .collection content with combined products.
            const collectionEl = (grid && grid.querySelector('.collection')) || document.querySelector('.collection.filter-transition-fast') || document.querySelector('.collection');
            if (collectionEl) {
              const wrapper = document.createElement('div');
              wrapper.className = 'collection__main';
              wrapper.innerHTML = result.products.innerHTML;
              collectionEl.innerHTML = '';
              collectionEl.appendChild(wrapper);
              if (typeof console !== 'undefined' && console.log) {
                console.log('[Filter OR] Replaced .collection content with product grid at:', collectionEl);
              }
            } else if (grid) {
              const main = document.createElement('div');
              main.className = 'collection__main';
              main.innerHTML = result.products.innerHTML;
              const hero = grid.querySelector('.collection-hero-top');
              if (hero && hero.nextElementSibling) {
                grid.insertBefore(main, hero.nextElementSibling);
              } else {
                grid.appendChild(main);
              }
              if (typeof console !== 'undefined' && console.log) {
                console.log('[Filter OR] Appended .collection__main to #ProductGridContainer (no .collection found)');
              }
            } else {
              if (typeof console !== 'undefined' && console.warn) {
                console.warn('[Filter OR] No target: looked for #ProductGridContainer .collection, .collection');
              }
            }
          }
        }
        
        // Update filters if available
        if (result.filters) {
          const existingFilters = document.querySelector('.collection-filter');
          if (existingFilters) {
            // Preserve current accordion states
            if (typeof window.AccordionManager !== 'undefined') {
              window.AccordionManager.saveAccordionStates();
            }
            Utils.updateDOMEfficiently(existingFilters, result.filters.innerHTML);
            // Restore accordion states after update using requestAnimationFrame
            requestAnimationFrame(() => {
              if (typeof window.AccordionManager !== 'undefined') {
                window.AccordionManager.restoreAccordionStates();
              }
            });
          }
        }
        
        // Update pagination if available (scope to grid so we don't touch nav elsewhere)
        if (result.pagination && result.pagination.innerHTML) {
          const grid = document.getElementById('ProductGridContainer');
          const root = grid || document;
          const existingPagination = root.querySelector('.collection__main .pagination, .collection__main [data-pagination], .pagination, [data-pagination]');
          if (existingPagination) {
            let paginationHTML = result.pagination.innerHTML;
            if (existingPagination.tagName === 'NAV' && /^\s*<nav[\s>]/i.test(paginationHTML)) {
              const innerMatch = paginationHTML.match(/<nav[^>]*>([\s\S]*?)<\/nav>\s*$/i);
              if (innerMatch) paginationHTML = innerMatch[1];
            }
            Utils.updateDOMEfficiently(existingPagination, paginationHTML);
          }
        }
        
        // Update product count if available
        if (result.productCount) {
          const existingCount = document.querySelector('.collection-header__count, [data-product-count]');
          if (existingCount) {
            existingCount.textContent = result.productCount.textContent;
          }
        }
      } catch (error) {
        Utils.error('Error updating content optimally:', error);
        // Fallback to original update method
        this.updateContent(result.html);
      }
    },

    updateContent(data) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = data;
      
      // Try multiple selectors to find the product content
      const newProductList = tempDiv.querySelector('.collection .collection__main') ||
                            tempDiv.querySelector('.product-list') ||
                            tempDiv.querySelector('[id*="product-list"]') ||
                            tempDiv.querySelector('.collection__products-wrapper .collection');
      
      const newFilter = tempDiv.querySelector('.collection-filter');
      
      // Try multiple selectors for existing content
      const existingProductList = document.querySelector('.collection .collection__main') ||
                                 document.querySelector('.product-list') ||
                                 document.querySelector('[id*="product-list"]');
      
      const existingFilter = document.querySelector('.collection-filter');

      // Update products instantly without animations
      if (newProductList && existingProductList) {
        // Clear existing content first to prevent overlapping
        existingProductList.innerHTML = '';
        
        // Replace content immediately for faster response
        existingProductList.innerHTML = newProductList.innerHTML;
        // Ensure full opacity and remove any transitions
        existingProductList.style.opacity = '1';
        existingProductList.style.transition = 'none';
        existingProductList.style.transform = 'none';
        
        // Force a reflow to ensure content is rendered
        existingProductList.offsetHeight;

        // Wait for images and content to load before considering update complete
        this.waitForContentLoad(existingProductList);
      } else {
        Utils.error('Failed to find product content in new or existing DOM');
        
        // Fallback: try to update the entire collection container
        const newCollection = tempDiv.querySelector('.collection');
        const existingCollection = document.querySelector('.collection');
        
        if (newCollection && existingCollection) {
          existingCollection.innerHTML = newCollection.innerHTML;
          this.waitForContentLoad(existingCollection);
        }
      }
      
      // Update filter content but preserve the loading overlay and structure
      if (newFilter && existingFilter) {
        // Save the loading overlay
        const loadingOverlay = existingFilter.querySelector('.filter-loading-overlay');
        
        // Update the filter content
        existingFilter.innerHTML = newFilter.innerHTML;
        
        // Restore the loading overlay if it existed
        if (loadingOverlay) {
          existingFilter.insertBefore(loadingOverlay, existingFilter.firstChild);
        }
      }
    },

    waitForContentLoad(container) {
      // Find all images in the container
      const images = container.querySelectorAll('img');
      const totalImages = images.length;
      let loadedImages = 0;
      let contentLoadTimeout;
      
      // Get progress indicator element
      const progressIndicator = document.getElementById('loading-progress');

      // Function to dispatch contentLoaded event
      const dispatchContentLoaded = () => {
        if (contentLoadTimeout) {
          clearTimeout(contentLoadTimeout);
        }
        
        // Clear progress indicator
        if (progressIndicator) {
          progressIndicator.textContent = '';
        }
        
        const event = new CustomEvent('contentLoaded');
        document.dispatchEvent(event);
      };

      // If no images, content is ready immediately
      if (totalImages === 0) {
        // Use requestAnimationFrame for immediate dispatch
        requestAnimationFrame(dispatchContentLoaded);
        return;
      }
      
      // Track image loading
      const checkImageLoaded = () => {
        loadedImages++;

        // All images loaded
        if (loadedImages >= totalImages) {
          // Use requestAnimationFrame for smooth completion
          requestAnimationFrame(dispatchContentLoaded);
        }
      };
      
      // Set up image load listeners
      images.forEach((img, index) => {
        if (img.complete && img.naturalWidth > 0) {
          checkImageLoaded();
        } else {
          img.addEventListener('load', checkImageLoaded, { once: true });
          img.addEventListener('error', () => {
            checkImageLoaded();
          }, { once: true });
        }
      });
      
      // Fallback timeout - don't wait forever
      contentLoadTimeout = setTimeout(() => {
        dispatchContentLoaded();
      }, 4000); // 4 second maximum wait
    },

    reinitializeComponents(options = {}) {
      // Don't automatically show filter after AJAX - let user manually open it
      // This ensures the sidebar stays closed after filter selection
      
      // Remove any updating classes
      const main = document.querySelector(CONFIG.selectors.collectionMain);
      if (main) {
        main.classList.remove('updating');
      }
      
      // Remove opacity-low from show-filter button so it is clickable (e.g. after page 2 / AJAX content load)
      const showFilterBtn = document.getElementById('show_filter');
      if (showFilterBtn) showFilterBtn.classList.remove('opacity-low');
      
      // Restore accordion states immediately
      if (typeof window.AccordionManager !== 'undefined') {
        window.AccordionManager.restoreAccordionStates();
        window.AccordionManager.bindAccordionEvents();
      }
      
      // Reinitialize filter selection: only sync FROM DOM when not coming from Apply (after Apply, FilterState is already correct)
      if (typeof window.FilterSelectionManager !== 'undefined') {
        if (!options.skipFilterStateSync) {
          window.FilterSelectionManager.initializeAppliedFilters();
        }
        window.FilterSelectionManager.updateApplyButton();
      }

      // Update selected filters display
      // Use requestAnimationFrame to batch DOM updates
      requestAnimationFrame(() => {
        if (typeof window.SelectedFiltersManager !== 'undefined') {
          window.SelectedFiltersManager.updateDisplay();
        }
      });
      
      // Reinitialize other components
      if (typeof window.filterBar === 'function') {
        window.filterBar();
      }
      
      // Reinitialize Slick slider for mobile
      if (typeof $ !== 'undefined' && typeof $.fn.slick !== 'undefined') {
        // Destroy existing slick instance if it exists
        const filterBar = $('.filter-bar');
        if (filterBar.hasClass('slick-initialized')) {
          filterBar.slick('unslick');
        }
        
        // Reinitialize slick if on mobile
        if (window.innerWidth <= 700) {
          // Use requestAnimationFrame for DOM-ready initialization
          requestAnimationFrame(() => {
            if (typeof window.slickslider === 'function') {
              window.slickslider();
            } else {
              // Fallback slick initialization
              filterBar.slick({
                slidesToShow: 2,
                dots: true,
                mobileFirst: true,
                prevArrow: '<button class="slick-arrow slick-prev" aria-label="Previous filter"><svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 36 36" fill="none"><circle cx="18" cy="18" r="18" fill="#FFFFFF"></circle><path d="M21 12L13 18.5L21 25" stroke="#B6637B" stroke-width="3" stroke-linecap="round"></path></svg></button>',
                nextArrow: '<button class="slick-arrow slick-next" aria-label="Next filter"><svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 36 36" fill="none"><circle cx="18" cy="18" r="18" fill="#FFFFFF"></circle><path d="M21 12L13 18.5L21 25" stroke="#B6637B" stroke-width="3" stroke-linecap="round"></path></svg></button>',
                responsive: [{
                  breakpoint: 768,
                  settings: 'unslick',
                  slidesToShow: 2,
                }]
              });
            }
          });
        }
      }
    }
  };

  // ========================================
  // GLOBAL EXPORTS
  // ========================================
  
  // Expose AjaxFilter globally
  window.AjaxFilter = AjaxFilter;

  // Initialize when DOM is ready (if dependencies are available)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (typeof window.CollectionFilterSystem !== 'undefined') {
        // CollectionFilterSystem will call AjaxFilter.init()
      }
    });
  } else {
    if (typeof window.CollectionFilterSystem !== 'undefined') {
      // Ready
    }
  }

})();
